(() => {
  "use strict";

  const root = document.querySelector("[data-qe-lens-finder]");
  if (!root) return;

  const dataScript = document.getElementById("qeLensData");
  const input = root.querySelector("#qeLiveSearch");
  const suggestions = root.querySelector("#qeSuggestions");
  const tableBody = root.querySelector("#qeTableBody");
  const tabs = [...root.querySelectorAll(".qe-tab")];

  let lenses = [];
  let activeRetailer = "all";
  let activeSuggestion = -1;
  let currentMatches = [];

  /*
   * Add each new retailer logo here after uploading it to:
   * Shopify Admin → Content → Files
   */
  const retailerLogos = {
  specsavers:
    "https://cdn.shopify.com/s/files/1/0797/2139/8563/files/qe-retailer-specsavers_png.webp?v=1784482709",

  boots:
    "https://cdn.shopify.com/s/files/1/0797/2139/8563/files/qe-retailer-boots.png?v=1784485365",

  "vision-express":
    "https://cdn.shopify.com/s/files/1/0797/2139/8563/files/qe-retailer-vision-express.png?v=1784485366",

  "optical-express":
    "https://cdn.shopify.com/s/files/1/0797/2139/8563/files/qe-retailer-optical-express.webp?v=1784485365",

  "asda-opticians":
    "https://cdn.shopify.com/s/files/1/0797/2139/8563/files/qe-retailer-asda-opticians.png?v=1784485365",

  costco:
    "https://cdn.shopify.com/s/files/1/0797/2139/8563/files/qe-retailer-costco.jpg?v=1784485365",

  scrivens:
    "https://cdn.shopify.com/s/files/1/0797/2139/8563/files/qe-retailer-scrivens.webp?v=1784485365",

  "david-clulow":
    "https://cdn.shopify.com/s/files/1/0797/2139/8563/files/qe-retailer-david-clulow.png?v=1784485365"
};

  const escapeHtml = value =>
    String(value ?? "").replace(/[&<>"']/g, character => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;"
    }[character]));

  const searchAliases = {
    astigmatism: "toric",
    astigmatic: "toric"
  };

  function normaliseSearchWord(word) {
    if (
      word.length >= 3 &&
      (
        "astigmatism".startsWith(word) ||
        "astigmatic".startsWith(word)
      )
    ) {
      return "toric";
    }

    return searchAliases[word] || word;
  }

  const normaliseLiteral = value =>
    String(value ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();

  const normalise = value =>
    normaliseLiteral(value)
      .split(/\s+/)
      .filter(Boolean)
      .map(normaliseSearchWord)
      .join(" ");

  function retailerKey(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function retailerInitials(value) {
    return String(value || "QE")
      .split(/\s+/)
      .filter(Boolean)
      .map(word => word.charAt(0))
      .join("")
      .slice(0, 2)
      .toUpperCase();
  }

function getSearchText(lens) {
  return normalise([
    lens.ownBrand,
    lens.equivalent,
    lens.retailer,
    lens.manufacturer,
    lens.productTitle,
    lens.wear,
    lens.type,
    lens.searchKeywords,
    lens.slug
  ].join(" "));
}

function terminologyPreference(lens, query) {
  const literalQueryWords = normaliseLiteral(query)
    .split(" ")
    .filter(Boolean);
  const ownBrandWords = new Set(
    normaliseLiteral(lens.ownBrand).split(" ").filter(Boolean)
  );
  const hasAstigmatismIntent = literalQueryWords.some(word =>
    word.length >= 3 &&
    (
      "astigmatism".startsWith(word) ||
      "astigmatic".startsWith(word)
    )
  );
  const hasToricIntent = literalQueryWords.some(word =>
    word.length >= 3 && "toric".startsWith(word)
  );

  if (hasAstigmatismIntent) {
    return ownBrandWords.has("astigmatism") ||
      ownBrandWords.has("astigmatic")
      ? 20
      : 0;
  }

  if (hasToricIntent) {
    return ownBrandWords.has("toric") ? 20 : 0;
  }

  return 0;
}

function scoreLens(lens, query) {
    const searchQuery = normalise(query);

    if (!searchQuery) return 0;

    const ownBrand = normalise(lens.ownBrand);
    const equivalent = normalise(lens.equivalent);
    const retailer = normalise(lens.retailer);
    const manufacturer = normalise(lens.manufacturer);
    const keywords = normalise(lens.searchKeywords);
    const product = normalise(lens.productTitle);
    const searchText = getSearchText(lens);

    if (ownBrand === searchQuery) return 140;
    if (ownBrand.startsWith(searchQuery)) return 130;
    if (ownBrand.includes(searchQuery)) return 120;

    if (equivalent === searchQuery) return 115;
    if (equivalent.startsWith(searchQuery)) return 105;
    if (equivalent.includes(searchQuery)) return 95;

    if (keywords.includes(searchQuery)) return 95;
    if (product.includes(searchQuery)) return 92;
    if (retailer.includes(searchQuery)) return 85;
    if (manufacturer.includes(searchQuery)) return 82;

    const words = searchQuery.split(" ").filter(Boolean);
    const searchableWords = new Set(
      searchText.split(" ").filter(Boolean)
    );
    const matchedWords = words.filter(word =>
      searchableWords.has(word)
    ).length;

    /*
     * A multi-word query must match every word. Previously, one matching
     * word in "lacrima toric" scored 35 and allowed unrelated toric lenses
     * through the minimum score of 30.
     */
    if (words.length > 1 && matchedWords !== words.length) {
      return 0;
    }

    return matchedWords
      ? (matchedWords / words.length) * 70
      : 0;
  }

  function findMatches(query, limit = 8) {
    const searchQuery = normalise(query);
    const words = searchQuery.split(" ").filter(Boolean);

    const rankedMatches = lenses
      .map(lens => ({
        lens,
        score:
          scoreLens(lens, query) +
          terminologyPreference(lens, query)
      }))
      .filter(item => item.score >= 30)
      .sort((first, second) =>
        second.score - first.score ||
        String(first.lens.ownBrand || "").localeCompare(
          String(second.lens.ownBrand || "")
        )
      );

    if (!rankedMatches.length) return [];

    /*
     * If a multi-word phrase closely matches an own-brand name, show only
     * results in that same high-confidence score band. Broader multi-word
     * searches such as "monthly toric" can still return several records,
     * but every returned record must contain both words.
     */
    if (words.length > 1 && rankedMatches[0].score >= 120) {
      const bestScore = rankedMatches[0].score;

      return rankedMatches
        .filter(item => item.score >= bestScore - 5)
        .slice(0, limit);
    }

    return rankedMatches.slice(0, limit);
  }

  function closeSuggestions() {
    suggestions.innerHTML = "";
    suggestions.classList.remove("is-open");
    input.setAttribute("aria-expanded", "false");

    activeSuggestion = -1;
    currentMatches = [];
  }

  function renderRetailerLogo(lens) {
    const key = retailerKey(lens.retailer);
    const logoUrl = retailerLogos[key];

    if (logoUrl) {
      return `
        <span
          class="qe-match-retailer-logo"
          aria-label="${escapeHtml(lens.retailer)}"
        >
          <img
            src="${escapeHtml(logoUrl)}"
            alt="${escapeHtml(lens.retailer)}"
            loading="lazy"
          >
        </span>
      `;
    }

    return `
      <span
        class="qe-match-retailer-logo qe-match-retailer-fallback"
        aria-label="${escapeHtml(lens.retailer)}"
      >
        ${escapeHtml(retailerInitials(lens.retailer))}
      </span>
    `;
  }

  function renderProductImage(lens) {
    if (lens.productImage) {
      return `
        <span class="qe-match-product-image">
          <img
            src="${escapeHtml(lens.productImage)}"
            alt="${escapeHtml(
              lens.productTitle || lens.equivalent
            )}"
            loading="lazy"
          >
        </span>
      `;
    }

    return `
      <span class="qe-match-product-image qe-match-product-fallback">
        <small>QUALITY EYES</small>
        <strong>${escapeHtml(lens.equivalent)}</strong>
      </span>
    `;
  }

  function renderAvailability(lens) {
    if (lens.productAvailable === true) {
      return `
        <span class="qe-match-stock is-available">
          <span></span>
          Available to order
        </span>
      `;
    }

    return `
      <span class="qe-match-stock">
        Check availability
      </span>
    `;
  }

  function renderMatchCard(lens, index) {
    const destination = lens.href || "#";

    return `
      <a
        class="qe-product-match-card"
        href="${escapeHtml(destination)}"
        role="option"
        data-index="${index}"
        aria-selected="false"
      >
        <span class="qe-match-own-brand">
          ${renderRetailerLogo(lens)}

          <span class="qe-match-copy">
            <small>Your lens box</small>

            <strong>
              ${escapeHtml(lens.ownBrand)}
            </strong>

            <span>
              Private label by ${escapeHtml(lens.retailer)}
            </span>
          </span>
        </span>

        <span class="qe-match-arrow" aria-hidden="true">
          <span>→</span>
          <small>Your real lens</small>
        </span>

        <span class="qe-match-real-product">
          ${renderProductImage(lens)}

          <span class="qe-match-copy">
            <small>Manufacturer product</small>

            <strong>
              ${escapeHtml(lens.equivalent)}
            </strong>

            <span>
              by ${escapeHtml(lens.manufacturer)}
            </span>

          ${
  lens.matchStatus
    ? `
      <span class="qe-match-status">
        ✓ ${escapeHtml(lens.matchStatus)}
      </span>
    `
    : ""
}

            ${
  lens.productPrice && lens.productAvailable === true
    ? `
      <span class="qe-match-price">
        ${escapeHtml(lens.productPrice)}
      </span>
    `
    : ""
}

            ${renderAvailability(lens)}
          </span>
        </span>

        <span class="qe-match-shop">
          <span>
  View Product
</span>
          <strong aria-hidden="true">›</strong>
        </span>
      </a>
    `;
  }

  function renderSuggestions() {
    const query = input.value.trim();

    if (!query) {
      closeSuggestions();
      return;
    }

    currentMatches = findMatches(query);

    if (!currentMatches.length) {
      suggestions.innerHTML = `
        <div class="qe-suggestion-empty">
          <strong>No exact match found</strong>

          <span>
            Try another word from your contact lens box.
          </span>

          <a href="/pages/contact?subject=Lens%20Finder%20Help">
            Ask a qualified optician
          </a>
        </div>
      `;

      suggestions.classList.add("is-open");
      input.setAttribute("aria-expanded", "true");
      return;
    }

    suggestions.innerHTML = currentMatches
      .map((item, index) =>
        renderMatchCard(item.lens, index)
      )
      .join("");

    suggestions.classList.add("is-open");
    input.setAttribute("aria-expanded", "true");
    activeSuggestion = -1;
  }

  function moveSuggestion(direction) {
    const options = [
      ...suggestions.querySelectorAll(
        ".qe-product-match-card"
      )
    ];

    if (!options.length) return;

    activeSuggestion =
      (activeSuggestion + direction + options.length) %
      options.length;

    options.forEach((option, index) => {
      const isActive = index === activeSuggestion;

      option.classList.toggle("is-active", isActive);
      option.setAttribute(
        "aria-selected",
        String(isActive)
      );
    });

    options[activeSuggestion].scrollIntoView({
      block: "nearest"
    });
  }

  function openBestMatch() {
    const query = input.value.trim();

    if (!query) {
      input.focus();
      return;
    }

    const matches = findMatches(query, 1);

    if (!matches.length) {
      renderSuggestions();
      return;
    }

    const bestMatch = matches[0].lens;

    if (bestMatch.href) {
      window.location.href = bestMatch.href;
    }
  }

  function renderTable() {
    if (!tableBody) return;

    const filteredLenses =
      activeRetailer === "all"
        ? lenses
        : lenses.filter(
            lens => lens.retailerSlug === activeRetailer
          );

    tableBody.innerHTML = filteredLenses
      .map(lens => `
        <tr>
          <td>
            <span class="qe-retailer">
              ${escapeHtml(lens.retailer)}
            </span>
          </td>

          <td>
            <strong>
              ${escapeHtml(lens.ownBrand)}
            </strong>
          </td>

          <td>
            ${
              lens.href
                ? `
                  <a href="${escapeHtml(lens.href)}">
                    ${escapeHtml(lens.equivalent)}
                  </a>
                `
                : escapeHtml(lens.equivalent)
            }
          </td>

          <td>
            ${escapeHtml(lens.manufacturer)}
          </td>

          <td>
            ${escapeHtml(lens.wear)}
          </td>

          <td>
            ${escapeHtml(lens.type)}
          </td>
        </tr>
      `)
      .join("");
  }

  function setActiveRetailer(retailer, updateUrl = false) {
    const matchingTab = tabs.find(
      tab => tab.dataset.retailer === retailer
    );

    activeRetailer = matchingTab ? retailer : "all";

    tabs.forEach(tab => {
      tab.setAttribute(
        "aria-pressed",
        String(tab.dataset.retailer === activeRetailer)
      );
    });

    if (updateUrl && window.history?.replaceState) {
      const url = new URL(window.location.href);

      if (activeRetailer === "all") {
        url.searchParams.delete("retailer");
      } else {
        url.searchParams.set("retailer", activeRetailer);
      }

      url.hash = "qe-equivalents";
      window.history.replaceState({}, "", url);
    }

    renderTable();
  }

  function initialise() {
    try {
      if (!dataScript) {
        throw new Error(
          "Lens Finder data block was not found."
        );
      }

      lenses = JSON.parse(
        dataScript.textContent || "[]"
      );

      if (!Array.isArray(lenses) || !lenses.length) {
        throw new Error(
          "No active Lens Finder entries were found."
        );
      }

      const requestedRetailer = new URLSearchParams(
        window.location.search
      ).get("retailer");

      setActiveRetailer(requestedRetailer || "all");
    } catch (error) {
      console.error(
        "Quality Eyes Lens Finder:",
        error
      );

      suggestions.innerHTML = `
        <div class="qe-suggestion-empty">
          <strong>The Lens Finder could not load</strong>

          <span>
            Please refresh the page or contact Quality Eyes.
          </span>
        </div>
      `;
    }
  }

  input.addEventListener("input", renderSuggestions);
  input.addEventListener("focus", renderSuggestions);

  input.addEventListener("keydown", event => {
    const options = [
      ...suggestions.querySelectorAll(
        ".qe-product-match-card"
      )
    ];

    if (event.key === "ArrowDown" && options.length) {
      event.preventDefault();
      moveSuggestion(1);
      return;
    }

    if (event.key === "ArrowUp" && options.length) {
      event.preventDefault();
      moveSuggestion(-1);
      return;
    }

    if (event.key === "Escape") {
      closeSuggestions();
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();

      if (
        activeSuggestion >= 0 &&
        options[activeSuggestion]
      ) {
        window.location.href =
          options[activeSuggestion].href;
      } else {
        openBestMatch();
      }
    }
  });

  tabs.forEach(tab => {
    tab.addEventListener("click", () => {
      setActiveRetailer(tab.dataset.retailer, true);
    });
  });

  document.addEventListener("click", event => {
    if (
      !event.target.closest(".qe-autocomplete-wrap")
    ) {
      closeSuggestions();
    }
  });

  initialise();
})();
